import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useMobileMenu } from '../context/MobileMenuManager';

const Navbar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { 
    mobileMenuOpen, 
    showDashboardSection, 
    closeMobileMenu, 
    toggleMobileMenu,
    toggleDashboardSection 
  } = useMobileMenu();
  
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [showSearch, setShowSearch] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(null);
  
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  let closeTimeout = null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        closeMobileMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMobileMenu]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    setShowDropdown(false);
    closeMobileMenu();
    window.location.href = '/';
  };

  const handleMenuEnter = (menuId) => {
    clearTimeout(closeTimeout);
    setActiveMenu(menuId);
  };

  const handleMenuLeave = () => {
    closeTimeout = setTimeout(() => {
      setActiveMenu(null);
    }, 140);
  };

  const handleSearchToggle = () => {
    setShowSearch(!showSearch);
    closeMobileMenu();
  };

  const handleSearchClose = () => {
    setShowSearch(false);
  };

  const handleAccordionToggle = (accordionId) => {
    setActiveAccordion(activeAccordion === accordionId ? null : accordionId);
  };

  // Menu data structure للنافبار
  const menuData = {
    'artists-menu': {
      title: 'Artists',
      sections: [
        {
          title: 'Browse',
          links: [
            { text: 'Featured Artists', href: '#' },
            { text: 'New Artists', href: '#' },
            { text: 'All Artists', href: '#' }
          ]
        },
        {
          title: 'By Medium',
          links: [
            { text: 'Painting', href: '#' },
            { text: 'Sculpture', href: '#' },
            { text: 'Photography', href: '#' },
            { text: 'Mixed Media', href: '#' },
            { text: 'Digital Art', href: '#' }
          ]
        },
        {
          title: 'By Style',
          links: [
            { text: 'Abstract', href: '#' },
            { text: 'Minimalist', href: '#' },
            { text: 'Conceptual', href: '#' },
            { text: 'Contemporary', href: '#' },
            { text: 'Figurative', href: '#' }
          ]
        }
      ],
      image: {
        src: "https://storage.googleapis.com/uxpilot-auth.appspot.com/c2bc4e3993-bf32e2c32fdaf2adf23f.png",
        alt: "contemporary artist working in minimalist studio with abstract painting",
        title: "Featured Artist",
        description: "Discover emerging voices"
      }
    },
    'collections-menu': {
      title: 'Collections',
      sections: [
        {
          title: 'Explore',
          links: [           
            { text: 'Curated Selections', href: '#' },
            { text: 'Limited Editions', href: '#' },
            { text: 'New Acquisitions', href: '#' },
            { text: 'Notable Works', href: '#' }
          ]
        }
      ],
      image: {
        src: "https://storage.googleapis.com/uxpilot-auth.appspot.com/9ecbd07aeb-df4a662c5ed9e2961a5e.png",
        alt: "curated contemporary art pieces arranged in sophisticated white space",
        title: "Curated Selection",
        description: "Exceptional works, carefully chosen"
      }
    },
    'artwork-menu': {
      title: 'Artwork',
      sections: [
        {
          title: 'Browse',
          links: [
            { text: 'All Works', href: '#' },
            { text: 'Curators Selection', href: '#' },
            { text: 'New Works', href: '#' }
          ]
        },
        {
          title: 'By Medium',
          links: [
            { text: 'Painting', href: '#' },
            { text: 'Sculpture', href: '#' },
            { text: 'Photography', href: '#' },
            { text: 'Works on Paper', href: '#' }
          ]
        },
        {
          title: 'By Style',
          links: [
            { text: 'Abstract', href: '#' },
            { text: 'Contemporary', href: '#' },
            { text: 'Minimalist', href: '#' }
          ]
        },
        {
          title: 'By Price',
          links: [
            { text: 'Under $5,000', href: '#' },
            { text: '$5,000 - $15,000', href: '#' },
            { text: '$15,000 - $50,000', href: '#' },
            { text: 'Above $50,000', href: '#' }
          ]
        }
      ]
    },
    'inspiration-menu': {
      title: 'Inspiration',
      sections: [
        {
          title: 'Art Discourse',
          links: [
            { text: 'Editorial', href: '#' },
            { text: 'In The Studio', href: '#' },
            { text: 'Artists Resources', href: '#' }
          ]
        },
        {
          title: 'Exhibitions',
          links: [
            { text: 'Solo Presentations', href: '#' },
            { text: 'Group Presentations', href: '#' },
            { text: 'Artsy Presentations', href: '#' }
          ]
        }
      ],
      image: {
        src: "https://storage.googleapis.com/uxpilot-auth.appspot.com/2374271478-5b78257ae8d052d1142f.png",
        alt: "contemporary art exhibition in minimalist gallery space with white walls",
        title: "Current Exhibition",
        description: "View our latest presentations"
      }
    },
    'services-menu': {
      title: 'Services',
      sections: [
        {
          title: 'Art Advisory',
          links: [
            { text: 'Consultation', href: '#' },
            { text: 'Collection Building', href: '#' }
          ]
        },
        {
          title: 'Custom Commissions',
          links: [
            { text: 'Commission Process', href: '#' },
            { text: 'Inquire', href: '#' }
          ]
        }
      ]
    },
    'help-menu': {
      title: 'Help Centre',
      sections: [
        {
          title: 'For Collectors',
          links: [
            { text: 'Legal Guidelines', href: '#' },
            { text: 'For Art Collectors', href: '#' },
            { text: 'Acquisitions & Returns', href: '#' }
          ]
        },
        {
          title: 'For Artists',
          links: [
            { text: 'Become an Art Seller', href: '#' }
          ]
        },
        {
          title: 'Contact',
          links: [
            { text: 'Contact Support', href: '#' },
            { text: 'Contact Curators', href: '#' }
          ]
        }
      ]
    }
  };

  // بيانات قائمة الداشبورد
  const dashboardMenuItems = [
    { icon: 'fa-chart-line', label: 'Dashboard Overview', path: '/dashboard' },
    { icon: 'fa-image', label: 'My Collection', path: '/dashboard/collection' },
    { icon: 'fa-handshake', label: 'Offers & Orders', path: '/dashboard/offers' },
    { icon: 'fa-file-invoice', label: 'Invoices & Payments', path: '/dashboard/invoices' },
    { icon: 'fa-calendar-days', label: 'Exhibitions & Events', path: '/dashboard/events' },
    { icon: 'fa-bell', label: 'Notifications', path: '/dashboard/notifications' },
    { icon: 'fa-chart-bar', label: 'Reports & Insights', path: '/dashboard/reports' },
    { icon: 'fa-user-check', label: 'Following', path: '/dashboard/following' },
  ];

  const dashboardBottomMenuItems = [
    { icon: 'fa-gear', label: 'Account & Settings', path: '/dashboard/settings' },
    { icon: 'fa-circle-question', label: 'Support', path: '/dashboard/support' },
  ];

  const searchSuggestions = {
    artists: ['Anya Sharma', 'Chen Wei', 'David Kim', 'Elena Petrova'],
    collections: ['Contemporary Abstraction', 'Digital & New Media', 'Minimalist Forms', 'Textural Explorations'],
    exhibitions: ['Color & Space', 'Material Dialogues', 'Digital Synthesis', 'Liminal Spaces']
  };

  const renderMegaMenuContent = (menuId, menu) => {
    switch(menuId) {
      case 'artists-menu':
        return (
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-2">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                Browse
              </h3>
              <ul className="space-y-3">
                {menu.sections[0].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                By Medium
              </h3>
              <ul className="space-y-3">
                {menu.sections[1].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                By Style
              </h3>
              <ul className="space-y-3">
                {menu.sections[2].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-4">
              <div className="h-56 overflow-hidden relative bg-gray-50">
                <img
                  src={menu.image.src}
                  alt={menu.image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <p className="text-white text-xs font-light tracking-wide mb-1">
                    {menu.image.title}
                  </p>
                  <p className="text-white/80 text-xs font-light">
                    {menu.image.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'artwork-menu':
        return (
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-2">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                Browse
              </h3>
              <ul className="space-y-3">
                {menu.sections[0].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                By Medium
              </h3>
              <ul className="space-y-3">
                {menu.sections[1].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                By Style
              </h3>
              <ul className="space-y-3">
                {menu.sections[2].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-4">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                By Price
              </h3>
              <ul className="space-y-3">
                {menu.sections[3].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );

      case 'collections-menu':
        return (
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                Explore
              </h3>
              <ul className="space-y-3">
                {menu.sections[0].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-5">
              <div className="h-56 overflow-hidden relative bg-gray-50">
                <img
                  src={menu.image.src}
                  alt={menu.image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <p className="text-white text-xs font-light tracking-wide mb-1">
                    {menu.image.title}
                  </p>
                  <p className="text-white/80 text-xs font-light">
                    {menu.image.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'inspiration-menu':
        return (
          <div className="grid grid-cols-12 gap-10">
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                Art Discourse
              </h3>
              <ul className="space-y-3">
                {menu.sections[0].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-3">
              <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                Exhibitions
              </h3>
              <ul className="space-y-3">
                {menu.sections[1].links.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                      {link.text}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div className="col-span-6">
              <div className="h-56 overflow-hidden relative bg-gray-50">
                <img
                  src={menu.image.src}
                  alt={menu.image.alt}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-5">
                  <p className="text-white text-xs font-light tracking-wide mb-1">
                    {menu.image.title}
                  </p>
                  <p className="text-white/80 text-xs font-light">
                    {menu.image.description}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="grid grid-cols-12 gap-10">
            {menu.sections.map((section, index) => (
              <div key={index} className="col-span-4">
                <h3 className="text-xs font-medium text-gray-900 mb-6 tracking-widest uppercase">
                  {section.title}
                </h3>
                <ul className="space-y-3">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <a href={link.href} className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1">
                        {link.text}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        );
    }
  };

  const renderMobileAccordionContent = (menuId, menu) => {
    return (
      <div className="pb-4 pl-4 space-y-3">
        {menu.sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="mb-4">
            {!(menuId === 'collections-menu' && section.title === 'Explore') && (
              <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">
                {section.title}
              </p>
            )}
            <div className="space-y-2">
              {section.links.map((link, linkIndex) => (
                <a 
                  key={linkIndex}
                  href={link.href} 
                  className="block text-sm text-gray-600 py-1 hover:text-gray-900 transition-colors"
                  onClick={closeMobileMenu}
                >
                  {link.text}
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };
  
  return (
    <>
      <header 
        id="header"
        className="relative top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 hidden lg:block"
        ref={menuRef}
      >
        <div className="w-full px-12">
          <div className="flex items-center justify-between py-5">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo5.png" 
                  alt="ArtRewards" 
                  className="h-12 w-auto object-contain"
                />
              </Link>
            </div>

            <nav className="flex items-center space-x-10 text-sm">
              {Object.entries(menuData).map(([menuId, menu]) => (
                <div key={menuId} className="relative group">
                  <button 
                    data-menu={menuId}
                    className="nav-link nav-trigger text-gray-700 hover:text-gray-900 transition-colors py-2"
                    onMouseEnter={() => handleMenuEnter(menuId)}
                  >
                    {menu.title}
                  </button>
                </div>
              ))}
            </nav>

            <div className="flex items-center space-x-6 text-sm">
              <button 
                id="search-trigger"
                className="text-gray-700 hover:text-gray-900 transition-colors p-2"
                onClick={handleSearchToggle}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>

              {isAuthenticated ? (
                <div className="relative" ref={dropdownRef}>
                  <button 
                    onClick={() => setShowDropdown(!showDropdown)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 transition-colors px-4 py-2 rounded-md hover:bg-gray-100"
                  >
                   <span className="font-medium inline-flex items-center gap-1">
                      <svg 
                        className="w-5 h-5 text-gray-900"
                        fill="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path 
                          d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                        />
                      </svg>
                      {user?.firstname || user?.name || 'User'}
                    </span>

                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {showDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                      <Link 
                        to="/dashboard" 
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setShowDropdown(false)}
                      >
                        Dashboard
                      </Link>
                      <Link 
                        to="/profile" 
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setShowDropdown(false)}
                      >
                        Profile
                      </Link>
                      <Link 
                        to="/settings" 
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                        onClick={() => setShowDropdown(false)}
                      >
                        Settings
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <Link 
                    to="/signin" 
                    className="text-gray-700 hover:text-gray-900 transition-colors py-2"
                  >
                    Sign In
                  </Link>
                  <Link 
                    to="/signup" 
                    className="bg-black text-white px-5 py-2.5 hover:bg-gray-800 transition-colors"
                  >
                    Join
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>

        {Object.entries(menuData).map(([menuId, menu]) => (
          <div
            key={menuId}
            id={menuId}
            className={`mega-menu absolute top-full left-0 right-0 mt-0 ${
              activeMenu === menuId ? 'active' : ''
            }`}
            onMouseEnter={() => handleMenuEnter(menuId)}
            onMouseLeave={handleMenuLeave}
          >
            <div className="w-full px-12 py-4">
              <div className="bg-white border-t border-gray-100 shadow-sm p-12">
                {renderMegaMenuContent(menuId, menu)}
              </div>
            </div>
          </div>
        ))}
      </header>

      {/* Mobile Header - تم التبسيط */}
      <header className="relative top-0 left-0 right-0 z-50 bg-white border-b border-gray-100 lg:hidden">
        <div className="w-full px-4">
          <div className="flex items-center justify-between py-4">
            {/* زر الهامبرجر دائماً موجود */}
            <button 
              id="mobile-menu-trigger" 
              className="text-gray-700 p-2"
              onClick={toggleMobileMenu}
            >
              {mobileMenuOpen ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="3" y1="12" x2="21" y2="12"></line>
                  <line x1="3" y1="6" x2="21" y2="6"></line>
                  <line x1="3" y1="18" x2="21" y2="18"></line>
                </svg>
              )}
            </button>
            
            {/* اللوجو دائماً في المنتصف */}
            <Link to="/" className="flex items-center">
              <img 
                src="/logo5.png" 
                alt="ArtRewards" 
                className="w-28 h-7 object-contain"
              />
            </Link>
            
            {/* زر البحث فقط - لا صورة بروفايل */}
            <div className="flex items-center">
              <button 
                id="search-trigger"
                className="text-gray-700 p-2"
                onClick={handleSearchToggle}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu (قائمة واحدة للكل) */}
      <div 
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`mobile-menu fixed top-0 left-0 bottom-0 w-4/5 max-w-sm bg-white z-50 overflow-y-auto shadow-2xl lg:hidden ${
          mobileMenuOpen ? 'active' : ''
        }`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-8">
            <span className="text-lg font-light tracking-wide text-gray-900">
              {isAuthenticated ? (user?.firstname || user?.name || 'User') : 'Menu'}
            </span>
            <button 
              id="mobile-menu-close" 
              className="text-gray-700 p-2"
              onClick={closeMobileMenu}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          {/* قسم تسجيل الدخول/التسجيل (في الأعلى لغير المسجلين) */}
          {!isAuthenticated && (
            <div className="mb-8 space-y-3">
              <Link 
                to="/signin" 
                className="block text-sm text-gray-700 py-2 hover:text-gray-900 transition-colors"
                onClick={closeMobileMenu}
              >
                Sign In
              </Link>
              <Link 
                to="/signup" 
                className="block bg-black text-white text-center px-5 py-3 text-sm hover:bg-gray-800 transition-colors"
                onClick={closeMobileMenu}
              >
                Join
              </Link>
            </div>
          )}
          
          {/* قسم النافبار (يظهر للجميع) */}
          <nav className="space-y-1 mb-8">
            <h3 className="text-xs uppercase tracking-widest text-gray-400 mb-4">Main Menu</h3>
            {Object.entries(menuData).map(([menuId, menu]) => (
              <div key={menuId} className="border-b border-gray-100">
                <button
                  onClick={() => handleAccordionToggle(menuId)}
                  className="accordion-trigger w-full flex items-center justify-between py-4 text-left text-gray-900"
                >
                  <span className="text-sm">{menu.title}</span>
                  <svg 
                    className={`w-4 h-4 text-gray-400 transition-transform ${
                      activeAccordion === menuId ? 'rotate-180' : ''
                    }`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                <div 
                  className={`accordion-content ${
                    activeAccordion === menuId ? 'active' : ''
                  }`}
                >
                  {renderMobileAccordionContent(menuId, menu)}
                </div>
              </div>
            ))}
          </nav>
          
          {/* قسم الداشبورد (يظهر فقط للمسجلين) */}
          {isAuthenticated && (
            <div className="mb-8">
              <button
                onClick={toggleDashboardSection}
                className="w-full flex items-center justify-between py-4 text-left text-gray-900 border-b border-gray-100"
              >
                <div className="flex items-center space-x-3">
                  <svg 
                    className="w-5 h-5 text-gray-600"
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                    />
                  </svg>
                  <span className="text-sm font-medium">My Dashboard</span>
                </div>
                <svg 
                  className={`w-4 h-4 text-gray-400 transition-transform ${showDashboardSection ? 'rotate-180' : ''}`}
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              <div className={`accordion-content ${showDashboardSection ? 'active' : ''}`}>
                <div className="py-4 pl-4 space-y-3">
                  {/* قسم الداشبورد الرئيسي */}
                  <div className="mb-4">
                    <div className="space-y-2">
                      {dashboardMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={closeMobileMenu}
                          className="flex items-center space-x-3 text-sm text-gray-600 py-2 hover:text-gray-900 transition-colors"
                        >
                          <i className={`fa-solid ${item.icon} w-5 text-xs`}></i>
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                  
                  {/* قسم الإعدادات والدعم */}
                  <div className="pt-4 border-t border-gray-100">
                    <div className="space-y-2">
                      {dashboardBottomMenuItems.map((item, index) => (
                        <Link
                          key={index}
                          to={item.path}
                          onClick={closeMobileMenu}
                          className="flex items-center space-x-3 text-sm text-gray-600 py-2 hover:text-gray-900 transition-colors"
                        >
                          <i className={`fa-solid ${item.icon} w-5 text-xs`}></i>
                          <span>{item.label}</span>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* زر تسجيل الخروج (يظهر فقط للمسجلين في الأسفل) */}
          {isAuthenticated && (
            <div className="mt-8 pt-6 border-t border-gray-100">
              <button 
                onClick={handleLogout}
                className="block w-full text-left text-sm text-gray-700 py-3 hover:text-gray-900 transition-colors"
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
      <div 
        id="mobile-menu-overlay"
        className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-all duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
        }`}
        onClick={closeMobileMenu}
      />
      <div 
        id="search-overlay"
        className={`search-overlay fixed inset-0 bg-white z-50 transition-all duration-300 ${
          showSearch ? 'opacity-100 visible pointer-events-auto' : 'opacity-0 invisible pointer-events-none'
        }`}
      >
        <div className="w-full h-full flex items-start justify-center pt-20 lg:pt-32">
          <div className="w-full max-w-3xl px-4 lg:px-8">
            <div className="relative">
              <input 
                type="text" 
                id="search-input"
                placeholder="Search artists, collections, exhibitions, artworks..."
                autoComplete="off"
                className="w-full text-lg lg:text-2xl font-light py-4 lg:py-6 px-4 pr-12 border-b border-gray-300 focus:border-gray-900 outline-none transition-colors bg-transparent"
              />
              <button 
                id="search-close"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors p-2"
                onClick={handleSearchClose}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            
            <div id="search-suggestions" className="mt-8 lg:mt-16">
              <div className="space-y-6 lg:grid lg:grid-cols-3 lg:gap-16 lg:space-y-0">
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4 lg:mb-6">
                    Artists
                  </h4>
                  <ul className="space-y-3 text-sm">
                    {searchSuggestions.artists.map((artist, index) => (
                      <li key={index}>
                        <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">
                          {artist}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4 lg:mb-6">
                    Collections
                  </h4>
                  <ul className="space-y-3 text-sm">
                    {searchSuggestions.collections.map((collection, index) => (
                      <li key={index}>
                        <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">
                          {collection}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h4 className="text-xs uppercase tracking-widest text-gray-400 mb-4 lg:mb-6">
                    Exhibitions
                  </h4>
                  <ul className="space-y-3 text-sm">
                    {searchSuggestions.exhibitions.map((exhibition, index) => (
                      <li key={index}>
                        <a href="#" className="text-gray-600 hover:text-gray-900 transition-colors">
                          {exhibition}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .mobile-menu {
          transform: translateX(-100%);
          transition: transform 300ms ease;
        }
        
        .mobile-menu.active {
          transform: translateX(0);
        }
        
        .mega-menu {
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition: opacity 200ms ease, visibility 200ms ease;
        }
        
        .mega-menu.active {
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
        }
        
        .search-overlay {
          transition: opacity 300ms ease, visibility 300ms ease;
        }
        
        .accordion-content {
          max-height: 0;
          overflow: hidden;
          transition: max-height 300ms ease;
        }
        
        .accordion-content.active {
          max-height: 500px;
        }
      `}</style>
    </>
  );
};

export default Navbar;